import type {
  CreateProfileArgs,
  DeleteProfileArgs,
  MappingArgs,
  ProfileData,
  UpdateProfileArgs,
  UploadArgs,
  ValidateArgs,
} from '../api/importApi'
import { stashUpload } from '../api/uploadRegistry'
import { newId } from '../../pricing/model/ids'
import type { CommandAttempt } from '../../pricing/model/types'
import type { MappingEditorVm, ProfileDraft, UploadAttempt, UploadParams, WizardVm } from './types'

/** Сборка аргументов команд импорта из vm и намерения (чистые функции, F05). */

const trimmed = (value: string): string | null => (value.trim() === '' ? null : value.trim())
const integer = (value: string): number | null => (/^\d+$/.test(value.trim()) ? Number(value.trim()) : null)

/** Отпечаток загрузки: тот же файл с теми же параметрами после обрыва получает те же ключи. */
export function uploadFingerprint(file: Pick<File, 'name' | 'size' | 'lastModified'>, params: UploadParams): string {
  return [file.name, file.size, file.lastModified, params.sheet, params.headerRow, params.profileId, params.supplier].join('|')
}

export interface UploadPlan {
  fingerprint: string
  attempt: CommandAttempt
  args: UploadArgs
}

/**
 * Загрузка накладной. Файл откладывается в реестр загрузок (в Redux он не
 * попадает), в аргументе — токен. `null` — ревизия базы ещё не известна.
 */
export function planUpload(
  vm: WizardVm,
  file: File,
  known: UploadAttempt | undefined,
): UploadPlan | null {
  if (vm.configurationVersion === null) return null
  const params = vm.upload.params
  const fingerprint = uploadFingerprint(file, params)
  const attempt = known !== undefined && known.fingerprint === fingerprint ? known.attempt : { operationId: newId(), idempotencyKey: newId() }
  const uploadToken = newId()
  stashUpload(uploadToken, file)
  return {
    fingerprint,
    attempt,
    args: {
      kind: 'upload',
      resourceId: vm.resourceId,
      configurationVersion: vm.configurationVersion,
      uploadToken,
      sheet: trimmed(params.sheet),
      headerRow: integer(params.headerRow),
      profileId: trimmed(params.profileId),
      supplier: trimmed(params.supplier),
      ...attempt,
    },
  }
}

/** Применить разметку колонок. Колонки без роли не отправляются (сервер их не ждёт). */
export function buildMappingArgs(
  vm: WizardVm,
  editor: MappingEditorVm,
  fileId: string,
  attempt: CommandAttempt,
): MappingArgs | null {
  if (vm.configurationVersion === null || !editor.canApply) return null
  const draft = editor.draft
  return {
    kind: 'mapping',
    resourceId: vm.resourceId,
    configurationVersion: vm.configurationVersion,
    fileId,
    ...attempt,
    data: {
      columns: editor.columns
        .filter((column) => column.role !== 'None')
        .map((column) => ({ columnIndex: column.columnIndex, role: column.role })),
      sheetName: trimmed(editor.sheetName),
      headerRowIndex: integer(editor.headerRow),
      saveAsProfile: draft.saveAsProfile,
      profileId: null,
      profileName: draft.saveAsProfile ? trimmed(draft.profileName) : null,
      supplierName: draft.saveAsProfile ? trimmed(draft.supplierName) : null,
      defaultUnit: trimmed(draft.defaultUnit),
      defaultVatRate: trimmed(draft.defaultVatRate),
      priceIncludesVat: draft.priceIncludesVat,
      quantityInPackages: draft.quantityInPackages,
    },
  }
}

export function buildProfileData(draft: ProfileDraft): ProfileData {
  return {
    name: draft.name.trim(),
    columns: draft.columns.map((column) => ({
      role: column.role,
      headerText: trimmed(column.headerText),
      columnIndex: integer(column.columnIndex),
      isRequiredOverride: null,
    })),
    supplierName: trimmed(draft.supplierName),
    sheetNameHint: trimmed(draft.sheetNameHint),
    headerRowIndex: integer(draft.headerRowIndex),
    defaultUnit: trimmed(draft.defaultUnit),
    defaultVatRate: trimmed(draft.defaultVatRate),
    priceIncludesVat: draft.priceIncludesVat,
    quantityInPackages: draft.quantityInPackages,
  }
}

export function buildCreateProfileArgs(
  resourceId: string,
  configurationVersion: string,
  draft: ProfileDraft,
  attempt: CommandAttempt,
): CreateProfileArgs {
  return { resourceId, configurationVersion, data: buildProfileData(draft), ...attempt }
}

export function buildUpdateProfileArgs(
  resourceId: string,
  configurationVersion: string,
  profileId: string,
  draft: ProfileDraft,
  attempt: CommandAttempt,
): UpdateProfileArgs {
  return { resourceId, configurationVersion, profileId, data: buildProfileData(draft), ...attempt }
}

export function buildDeleteProfileArgs(
  resourceId: string,
  configurationVersion: string,
  profileId: string,
  attempt: CommandAttempt,
): DeleteProfileArgs {
  return { resourceId, configurationVersion, profileId, ...attempt }
}

export function buildValidateArgs(
  resourceId: string,
  configurationVersion: string,
  fileId: string,
  attempt: CommandAttempt,
): ValidateArgs {
  return { resourceId, configurationVersion, fileId, ...attempt }
}
